import { FC, useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useSubscriptionRefetch } from 'dibs-subscriptions-utils/exports/useSubscriptionRefetch';
import { useAuctionState } from 'dibs-auctions/exports/hooks';

// components
import { PdpSharedAuctionSubscriptionErrorLazy } from '../PdpSharedAuctionSubscriptionError/PdpSharedAuctionSubscriptionErrorLazy';

// types
import { PdpSharedAuctionSubscription_item$key as Item } from './__generated__/PdpSharedAuctionSubscription_item.graphql';

type Props = {
    item: Item;
    refetch: () => void;
};

type RelayVariables = {
    userId: string;
    itemId: string;
    isTrade: boolean;
    isClient: boolean;
    fetchUser: boolean;
    fetchAuction: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const MAX_REFETCHES = 5;

const subscription = graphql`
    subscription PdpSharedAuctionSubscription(
        $input: AuctionItemUpdateSubscriptionInput!
        $isTrade: Boolean
        $userId: String = ""
        $isClient: Boolean!
        $fetchUser: Boolean!
        $fetchAuction: Boolean = false
    ) {
        auctionItemUpdate(input: $input) {
            item {
                ...PdpSharedAuctionInfo_item
            }
        }
    }
`;

const itemFragment = graphql`
    fragment PdpSharedAuctionSubscription_item on Item {
        isUnpurchasable
        isSold
        relevantAuctionLot(page: pdp) {
            endDate
        }
        ...useAuctionState_item @arguments(page: pdp)
    }
`;

const CONNECTION_DOWN_MAX = 1000 * 60 * 1.5;

const shouldAlertImmediatly = (endDate?: string | null): boolean => {
    if (endDate) {
        return new Date(endDate).getTime() - new Date().getTime() <= CONNECTION_DOWN_MAX;
    }
    return false;
};

export const PdpSharedAuctionSubscription: FC<Props> = ({ refetch, item: itemRef }) => {
    const item = useFragment(itemFragment, itemRef);
    const { isSold, relevantAuctionLot, isUnpurchasable } = item || {};
    const endDate = relevantAuctionLot?.endDate;

    const { isClient, userId, isTrade, itemId, fetchUser, fetchAuction } = useSelector<
        ReduxStoreType,
        RelayVariables
    >(relay => relay?.relayVariables?.variables || {});
    const [hasSubscriptionError, setHasSubscriptionError] = useState<boolean>(false);
    const endDateRef = useRef<string | null | undefined>(endDate);

    const {
        isPreviewAuction,
        isUnavailableAuction,
        hasAuctionEndedWithBIN,
        hasAuctionEndedWithWinner,
    } = useAuctionState({ item });

    const itemIsUnpurchasable =
        isUnavailableAuction ||
        (hasAuctionEndedWithBIN && !isSold) || // item was sold with BIN and then canceled
        isUnpurchasable;
    const setupSubscription =
        !hasAuctionEndedWithWinner && !itemIsUnpurchasable && !isPreviewAuction && !isSold;

    const subscriptionConfig = useMemo(
        () => ({
            subscription,
            variables: {
                userId,
                isTrade,
                isClient,
                fetchUser,
                fetchAuction,
                input: {
                    itemId,
                },
            },
            onError: () => setHasSubscriptionError(true),
        }),
        [isClient, userId, isTrade, itemId, fetchUser, fetchAuction]
    );

    const { isConnected, reconnectRefetches, hadError } = useSubscriptionRefetch(
        subscriptionConfig,
        {
            skip: !setupSubscription,
            refetch,
            refetchOnReconnect: true,
            pollWhileDisconnected: true,
            maxReconnectRefetches: MAX_REFETCHES,
            maxPollingRefetches: MAX_REFETCHES,
        }
    );

    useEffect(() => {
        endDateRef.current = endDate;
    }, [endDate]);

    useEffect(() => {
        let errorTimer: ReturnType<typeof setTimeout> | null = null;
        if (setupSubscription && !isConnected && !errorTimer && hadError) {
            errorTimer = setTimeout(
                () => {
                    setHasSubscriptionError(true);
                },
                shouldAlertImmediatly(endDateRef.current) || reconnectRefetches >= MAX_REFETCHES
                    ? 0
                    : CONNECTION_DOWN_MAX
            );
        }

        return () => {
            if (errorTimer) {
                clearTimeout(errorTimer);
            }
        };
    }, [refetch, isConnected, setupSubscription, reconnectRefetches, hadError]);

    return <PdpSharedAuctionSubscriptionErrorLazy hasSubscriptionError={hasSubscriptionError} />;
};
